import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { getTranslations } from "../../locales";
import { TranslationProvider } from "../../contexts/TranslationContext";
import Header from "../../components/common/header/header";
import ModalContent from "./modalContent/modalContent";
import Loader from "../../components/common/loader/loader";
import OtpModalContent from "./OtpmodalContent/otpmodalContent";
import Toast from "../../components/common/toast/toast";
import QuoteExplanationModal from "./QuoteExplanationModal/quoteExplanationModal";
import { cacheManager } from "../../utilities/cacheManager";
import { CACHE_CONSTANTS } from "../../constants/cacheConstants";
import "./ez-quote.scss";
import DataRequest from "../../services/dataRequest";

// Types
import {
  Category,
  Attribute,
  Manufacturer,
  Model,
  Condition,
  SelectedAttribute,
  Item,
  OptionType,
  StoreAddress,
  Store,
  StoreOption,
  QuotationDetails,
} from "./ez-quote.types";
export type { Item } from "./ez-quote.types";

// Helpers
import {
  loadStoresData,
  storesData,
  renderHeaderText,
  resolveJewelryHeader,
  getIconComponent,
  getHeaderStep,
  validateZipAndGetStores,
  mapQuotationToItems,
  mapQuotationStoreToOptions,
} from "./ez-quote.helpers";
import { NEW_QUOTE_ENTRY_PATH } from "../../constants/brandUrls";

// Step components
import CategoryStep from "./steps/CategoryStep";
import AttributesStep from "./steps/AttributesStep";
import ConditionsStep from "./steps/ConditionsStep";
import SummaryStep from "./steps/SummaryStep";
import FinalReviewStep from "./steps/FinalReviewStep";
import { useLocalStoragePersistence } from "./hooks/useLocalStoragePersistence";
import { makeHandleBackPress } from "./hooks/useBackPress";
import { useFetchHandlers } from "./hooks/useFetchHandlers";
import { useItemHandlers } from "./hooks/useItemHandlers";
import { useQuoteHandlers } from "./hooks/useQuoteHandlers";

const EZQuote: React.FC = () => {
  // ----- State -----
  const [categories, setCategories] = useState<Category[]>([]);
  const quoteDetailsRef = useRef<HTMLDivElement>(null);
  const [categoryHistory, setCategoryHistory] = useState<Category[][]>([]);
  const categoriesFetchedRef = useRef<boolean>(false);
  const initialMountRef = useRef<boolean>(true);

  // Simple scrolling function
  const scrollToElement = useCallback((element: HTMLElement | null) => {
    if (!element) return false;

    try {
      element.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
      return true;
    } catch (error) {
      console.warn('Scrolling failed:', error);
      return false;
    }
  }, []);
  const [selectedCategoryParent, setSelectedCategoryParent] = useState<
    Category | null
  >(null);
  const [selectedRootCategory, setSelectedRootCategory] = useState<Category | null>(null);

  // Store states
  const [
    selectedStoreOption,
    setSelectedStoreOption,
  ] = useState<StoreOption | null>(null);
  const [allStores, setAllStores] = useState<Store[]>([]);

  // Flow states (internal steps remain 1-6)
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [attributeError, setattributeError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState<number>(2);
  const totalInternalSteps = 6; // unchanged internally

  const [currentAttributeIndex, setCurrentAttributeIndex] = useState(0);
  const [attributes, setAttributes] = useState<Attribute[]>([]);
  const [manufacturerOptions, setManufacturerOptions] = useState<
    Manufacturer[]
  >([]);
  const [modelOptions, setModelOptions] = useState<Model[]>([]);
  const [showIntroModal, setShowIntroModal] = useState<boolean>(false);
  const [isOtpModalVisible, setIsOtpModalVisible] = useState<boolean>(false);
  const [otpModalMode, setOtpModalMode] = useState<"phone" | "email">("phone");
  const [dropdownResetCounter, setDropdownResetCounter] = useState<number>(0);
  const [isQuoteModalVisible, setIsQuoteModalVisible] = useState<boolean>(
    false
  );
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [query, setQuery] = useState<string>("");
  const [isEditing, setIsEditing] = useState<boolean>(false);
  // Current item
  const [currentItem, setCurrentItem] = useState<{
    category: Category | null;
    attributes: { [key: string]: SelectedAttribute };
    condition: Condition | null;
    parentCategoryName?: string;
    goldTypeName?: string;
    itemTypeLabel?: string;
  }>({
    category: null,
    attributes: {},
    condition: null,
  });

  // Items
  const [items, setItems] = useState<Item[]>([]);
  const maxItems = 3;
  const [selectedCondition, setSelectedCondition] = useState<string | null>(
    null
  );
  const [quoteId, setquoteId] = useState<string | null>(null);
  const [brand, setBrand] = useState<string | null>(null);

  // Saved-quote view: when the host sets a `qid` attribute on <ez-quote>, the
  // widget skips the normal Instant Quote flow and shows that quote instead,
  // reusing FinalReviewStep (the same "estimated quote" screen the live flow
  // ends on) rather than a separate UI. isSavedQuoteModeRef is a ref (not just
  // the state below) because useLocalStoragePersistence's mount effect needs to
  // see it synchronously, before React re-renders with the state update.
  const isSavedQuoteModeRef = useRef<boolean>(false);
  const [isSavedQuoteMode, setIsSavedQuoteMode] = useState<boolean>(false);
  const [savedQuote, setSavedQuote] = useState<QuotationDetails | null>(null);
  const [savedQuoteLoading, setSavedQuoteLoading] = useState<boolean>(false);
  const [savedQuoteError, setSavedQuoteError] = useState<string | null>(null);

  // Translations — derived from brand, recalculates when brand changes
  const t = getTranslations(brand);
  const conditions: Condition[] = [
    { id: "A", title: t.ezquote.bestCondition, description: t.ezquote.bestConditionDesc, icon_name: "triple_uparrow.svg" },
    { id: "B", title: t.ezquote.goodCondition, description: t.ezquote.goodConditionDesc, icon_name: "double_uparrow.svg" },
    { id: "C", title: t.ezquote.fairCondition, description: t.ezquote.fairConditionDesc, icon_name: "uparrow.svg" },
  ];

  const [openEstimateCard, setopenEstimateCard] = useState<boolean>(false);
  const [openExpertReviewAccordion, setOpenExpertReviewAccordion] = useState<boolean>(false);
  const [totalEstimate, setTotalEstimate] = useState<{
    min: number;
    max: number;
  }>({ min: 0, max: 0 });
  const [draftItem, setDraftItem] = useState<{
    category: Category | null;
    attributes: { [key: string]: SelectedAttribute };
    condition: Condition | null;
  }>({
    category: null,
    attributes: {},
    condition: null,
  });

  // Quotation store address
  const [storeAddress, setStoreAddress] = useState<StoreAddress | null>(null);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastRetryAction, setToastRetryAction] = useState<
    (() => void) | undefined
  >(undefined);
  const [toastVariant, setToastVariant] = useState<'error' | 'primary'>('error');
  const [goldParentCategory, setGoldParentCategory] = useState<Category | null>(null);
  const [selectedGoldItemType, setSelectedGoldItemType] = useState<OptionType | null>(null);
  const [goldCurrentAttributeIndex, setGoldCurrentAttributeIndex] = useState<number>(0);

  const [hydrated, setHydrated] = useState(false);

  // Track email send count per quote (using quoteId as key)
  const [emailSendCount, setEmailSendCount] = useState<{ [quoteId: string]: number }>({});
  const [smsDeliveryStatus, setSmsDeliveryStatus] = useState<string | null>(null);

  // Add this to the component state (near other useState hooks):
  const [categoryItemState, setCategoryItemState] = useState<{
    [categoryId: number]: {
      attributes: { [key: string]: SelectedAttribute };
      condition: Condition | null;
    };
  }>({});

  // Add a ref to store the category being restored
  const restoringCategoryRef = useRef<Category | null>(null);
  // Display-only label for gold item type (e.g. "Other/I'm not sure") — never sent to API
  const itemTypeLabelRef = useRef<string | undefined>(undefined);

  // Add state for ZIP code from modal and nearby stores
  const [zipCodeFromModal, setZipCodeFromModal] = useState<string | null>(null);
  const [nearbyStores, setNearbyStores] = useState<StoreOption[]>([]);

  // Load store data from JSON
  useEffect(() => {
    loadStoresData().then(data => {
      setAllStores(data?.data as Store[]);
    });
  }, []);

  // Ensure scroll happens immediately when step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentStep]);

  // Fetches a saved quotation by id (see the qid-detection effect below).
  // On failure — including a 404/not-found — shows a retry-enabled toast, the
  // same pattern already used for other API calls in this file (fetchData in
  // useFetchHandlers.ts, getQuote in useQuoteHandlers.ts), rather than a
  // dead-end error with no way to recover from a transient failure.
  const fetchSavedQuote = useCallback((quoteIdToFetch: string) => {
    setSavedQuoteLoading(true);
    DataRequest.execute(`quotation/${quoteIdToFetch}`, { method: "GET" }, { "Content-Type": "application/json" })
      .then((response: any) => {
        if (response?.data?.success && response.data.payload) {
          const quote = response.data.payload as QuotationDetails;
          setSavedQuote(quote);
          // Set the real quoteId (not just local saved-quote state) so the
          // existing "Email me a copy" flow (handleEmailSent's emailSendCount
          // tracking) keys off the correct id, same as the live flow.
          setquoteId(quote.ezQuoteId);
          setSavedQuoteError(null);
        } else {
          const message = response?.data?.messages?.[0] || null;
          setSavedQuoteError(message);
          showToast(message || "Error fetching quote.", () => fetchSavedQuote(quoteIdToFetch));
        }
        // Set inline rather than in a .finally() — a .finally() lands in its
        // own microtask after .then(), so React can't batch it with the
        // updates above into a single render.
        setSavedQuoteLoading(false);
      })
      .catch((err: unknown) => {
        console.error("Error fetching saved quote:", err);
        setSavedQuoteError(null);
        showToast("Error fetching quote.", () => fetchSavedQuote(quoteIdToFetch));
        setSavedQuoteLoading(false);
      });
  }, []);

  useEffect(() => {
    const brandElement = document.querySelector("ez-quote");
    if (brandElement) {
      const brandAttr = brandElement.getAttribute("brand");
      setBrand(brandAttr);

      const qidAttr = brandElement.getAttribute("qid");
      if (qidAttr) {
        // Set synchronously (ref, not state) so useLocalStoragePersistence's
        // mount effect — declared later, but firing in this same effect flush —
        // sees it and skips restoring/persisting the normal flow's state.
        isSavedQuoteModeRef.current = true;
        setIsSavedQuoteMode(true);
        fetchSavedQuote(qidAttr);
        return;
      }
    }
    const hasShownIntroModal = localStorage.getItem(CACHE_CONSTANTS.CACHE_KEYS.INTRO_MODAL_SHOWN);
    if (!hasShownIntroModal) {
      setShowIntroModal(true);
      localStorage.setItem(CACHE_CONSTANTS.CACHE_KEYS.INTRO_MODAL_SHOWN, "true");
    }
  }, []);

  const handleValidateZipAndGetStores = (zipCode: string) =>
    validateZipAndGetStores(zipCode, allStores);

  const showToast = (message: string, retryAction?: () => void, variant: 'error' | 'primary' = 'error') => {
    setToastMessage(message);
    setToastRetryAction(() => retryAction);
    setToastVariant(variant);
    setToastVisible(true);
  };

  const hideToast = () => {
    setToastVisible(false);
    setToastMessage("");
    setToastRetryAction(undefined);
    setToastVariant('error');
  };

  const {
    fetchData, fetchCategories, resetAndFetchCategories, fetchAttributes, fetchManufacturers, fetchModels,
  } = useFetchHandlers({
    goldParentCategory, categoryItemState, isEditing,
    categoriesFetchedRef, initialMountRef, restoringCategoryRef, itemTypeLabelRef,
    setLoading, setError, setCategories, setCategoryHistory,
    setSelectedCategoryParent, setSelectedRootCategory,
    setAttributes, setManufacturerOptions, setModelOptions,
    setCurrentItem, setSelectedCondition, setCurrentAttributeIndex, setGoldCurrentAttributeIndex,
    showToast,
  });

  // ----- Handlers (Category/Item Flow) -----
  // Adjust handleCategoryPress to ensure categoryHistory is updated correctly
  const handleCategoryPress = (selectedCategory: Category) => {
    // A direct Jewelry metal (Gold, Silver, Platinum) is one that:
    // 1. Has "Jewelry" as its displayed parent, AND
    // 2. Is actually present in the currently displayed category list.
    // Condition 2 prevents handleGoldContinue's leaf sub-items (Ring, Necklace, etc.)
    // from being intercepted here — they are NOT in the Jewelry-level list, so
    // selectedCategoryParent stays "Jewelry" but the category itself is not.
    const isDirectJewelryMetal =
      selectedCategoryParent?.category_name === "Jewelry" &&
      categories.some((c) => c.category_id === selectedCategory.category_id);

    if (isDirectJewelryMetal) {
      // Always treat Jewelry sub-metals as precious-metals flow (Gold, Silver, Platinum).
      // Must come before is_leaf so leaf metals are not routed through the standard flow.
      window.scrollTo({ top: 0, behavior: "smooth" });
      setGoldParentCategory(selectedCategory);
      setSelectedGoldItemType(null);
      // Always stay on step 2 — renderGoldNarrowStep shows Gold's children as item types
      // for Silver/Platinum (no own children), and handleGoldContinue fetches the metal's own attributes
    } else if (selectedCategory.is_leaf) {
      // Standard leaf category (Electronics sub-type, or Gold/Silver/Platinum item type)
      restoringCategoryRef.current = selectedCategory;
      setManufacturerOptions([]);
      setModelOptions([]);
      fetchAttributes(selectedCategory.category_id);
      setCurrentStep(3);
      setIsEditing(false);
    } else {
      // Non-leaf, non-direct-Jewelry-metal: show child categories
      if (selectedCategory.childCategories && selectedCategory.childCategories.length > 0) {
        window.scrollTo({ top: 0, behavior: "smooth" });
        // Save current categories to history
        setCategoryHistory(prev => [...prev, categories]);
        if (categoryHistory.length === 0) {
          setSelectedRootCategory(selectedCategory);
        }
        // Set the child categories as current
        setCategories(selectedCategory.childCategories);
        setSelectedCategoryParent(selectedCategory);
      }
    }
  };

  const {
    clearAttributeValues, handleAttributeSelect, handleConditionSelect,
    handleEditItem, handleDeleteItem, handleAddItem, addItemToList, handleContinueStep3,
  } = useItemHandlers({
    attributes, currentItem, conditions, items, maxItems,
    categoryItemState, selectedRootCategory, currentAttributeIndex,
    goldParentCategory, isEditing,
    setCurrentItem, setCurrentAttributeIndex, setCategoryItemState, setSelectedCondition,
    setItems, setDraftItem, setIsEditing, setModelOptions, setCurrentStep,
    setGoldParentCategory, setSelectedGoldItemType, setGoldCurrentAttributeIndex,
    setattributeError, setError, setAttributes, setManufacturerOptions,
    setCategoryHistory, setSelectedCategoryParent, setSelectedRootCategory,
    fetchModels, fetchManufacturers, fetchAttributes, resetAndFetchCategories,
  });

  const {
    handleProceedToReview, handleOtpVerified, handleEmailSent,
  } = useQuoteHandlers({
    items, brand, quoteId, selectedStoreOption, nearbyStores,
    setLoading, setItems, setTotalEstimate, setError, setquoteId, setCurrentStep,
    setIsOtpModalVisible, setOtpModalMode, setStoreAddress,
    setZipCodeFromModal, setSelectedStoreOption, setNearbyStores, setEmailSendCount,
    setSmsDeliveryStatus, showToast,
  });

  const handleContinue = () => {
    if (currentStep === 2) {
      if (!currentItem.category) { setError(t.ezquote.errorSelectCategory); return; }
      setCurrentStep(3);
    } else if (currentStep === 3) {
      if (!handleContinueStep3()) return;
      setCurrentStep(4);
    } else if (currentStep === 4) {
      if (!currentItem.condition) { setError(t.ezquote.errorSelectCondition); return; }
      addItemToList();
      setCurrentStep(5);
    } else if (currentStep === 5) {
      handleProceedToReview();
    }
  };

  const resetForm = (showLoader: boolean = false) => {
    setItems([]);
    setTotalEstimate({ min: 0, max: 0 });
    setCurrentItem({ category: null, attributes: {}, condition: null });
    setIsEditing(false);
    setCategoryHistory([]);
    setSelectedCategoryParent(null);
    setSelectedRootCategory(null);
    setGoldParentCategory(null);
    setSelectedGoldItemType(null);
    setGoldCurrentAttributeIndex(0);
    resetAndFetchCategories(showLoader);
    setCurrentStep(2);
    setSelectedCondition(null);
    setZipCodeFromModal(null);
    setNearbyStores([]);
    setSelectedStoreOption(null);
    setAttributes([]);
    setManufacturerOptions([]);
    setModelOptions([]);
    setCurrentAttributeIndex(0);
    setCategoryItemState({});
    setSmsDeliveryStatus(null);
    // Clear only application state, preserve intro modal
    cacheManager.clearApplicationState();
    // Clear cache when resetting form
  };

  const handleBackPress = makeHandleBackPress(
    {
      currentStep, draftItem, attributes, currentItem, goldCurrentAttributeIndex,
      selectedCategoryParent, categoryHistory, categories, goldParentCategory,
      isOtpModalVisible, otpModalMode,
    },
    {
      setCurrentStep, setItems, setCurrentItem, setGoldCurrentAttributeIndex,
      setattributeError, setSelectedCategoryParent, setCategories, setCategoryHistory,
      setSelectedRootCategory, setGoldParentCategory, setSelectedGoldItemType,
      setError, fetchAttributes, resetAndFetchCategories, resetForm,
      setIsOtpModalVisible, setOtpModalMode,
    }
  );

  const renderStep = () => {
    switch (currentStep) {
      case 2:
        return (
          <CategoryStep
            categories={categories}
            selectedCategoryParent={selectedCategoryParent}
            goldParentCategory={goldParentCategory}
            selectedGoldItemType={selectedGoldItemType}
            openDropdownId={openDropdownId}
            setOpenDropdownId={setOpenDropdownId}
            handleCategoryPress={handleCategoryPress}
            getIconComponent={getIconComponent}
            fetchAttributes={fetchAttributes}
            setCurrentStep={setCurrentStep}
            setIsEditing={setIsEditing}
            restoringCategoryRef={restoringCategoryRef}
            itemTypeLabelRef={itemTypeLabelRef}
            setSelectedGoldItemType={setSelectedGoldItemType}
            setGoldParentCategory={setGoldParentCategory}
          />
        );
      case 3:
        return (
          <AttributesStep
            attributes={attributes}
            currentItem={currentItem}
            goldCurrentAttributeIndex={goldCurrentAttributeIndex}
            goldParentCategory={goldParentCategory}
            selectedGoldItemType={selectedGoldItemType}
            currentStep={currentStep}
            attributeError={attributeError}
            setattributeError={setattributeError}
            openDropdownId={openDropdownId}
            setOpenDropdownId={setOpenDropdownId}
            dropdownResetCounter={dropdownResetCounter}
            manufacturerOptions={manufacturerOptions}
            modelOptions={modelOptions}
            handleAttributeSelect={handleAttributeSelect}
            clearAttributeValues={clearAttributeValues}
            handleContinue={handleContinue}
            getIconComponent={getIconComponent}
            brand={brand}
            setCurrentItem={setCurrentItem}
            setItems={setItems}
            setDraftItem={setDraftItem}
            setIsEditing={setIsEditing}
            setCurrentStep={setCurrentStep}
            setGoldCurrentAttributeIndex={setGoldCurrentAttributeIndex}
            draftItem={draftItem}
            isEditing={isEditing}
          />
        );
      case 4:
        return (
          <ConditionsStep
            conditions={conditions}
            selectedCondition={selectedCondition}
            handleConditionSelect={handleConditionSelect}
            handleContinue={handleContinue}
            getIconComponent={getIconComponent}
          />
        );
      case 5:
        return (
          <SummaryStep
            items={items}
            maxItems={maxItems}
            getIconComponent={getIconComponent}
            handleEditItem={handleEditItem}
            handleDeleteItem={handleDeleteItem}
            handleAddItem={handleAddItem}
            handleProceedToReview={handleProceedToReview}
            openEstimateCard={openEstimateCard}
            setopenEstimateCard={setopenEstimateCard}
          />
        );
      case 6:
        return (
          <FinalReviewStep
            items={items}
            quoteId={quoteId}
            smsDeliveryStatus={smsDeliveryStatus}
            totalEstimate={totalEstimate}
            error={error}
            nearbyStores={nearbyStores}
            zipCodeFromModal={zipCodeFromModal}
            brand={brand}
            openExpertReviewAccordion={openExpertReviewAccordion}
            setOpenExpertReviewAccordion={setOpenExpertReviewAccordion}
            openEstimateCard={openEstimateCard}
            setopenEstimateCard={setopenEstimateCard}
            emailSendCount={emailSendCount}
            setOtpModalMode={setOtpModalMode}
            setIsOtpModalVisible={setIsOtpModalVisible}
            scrollToElement={scrollToElement}
            quoteDetailsRef={quoteDetailsRef}
            resetForm={resetForm}
            storeAddress={storeAddress}
            setIsQuoteModalVisible={setIsQuoteModalVisible}
            getIconComponent={getIconComponent}
          />
        );
      default:
        return <div>{t.ezquote.invalidStep}</div>;
    }
  };


  // --- localStorage persistence (restore on mount + save on change + cleanup) ---
  useLocalStoragePersistence(
    {
      hydrated,
      currentStep,
      categories,
      categoryHistory,
      selectedCategoryParent,
      selectedRootCategory,
      currentItem,
      items,
      isEditing,
      selectedCondition,
      currentAttributeIndex,
      attributes,
      manufacturerOptions,
      modelOptions,
      draftItem,
      totalEstimate,
      quoteId,
      brand,
      openEstimateCard,
      openExpertReviewAccordion,
      categoryItemState,
      zipCodeFromModal,
      nearbyStores,
      selectedStoreOption,
      emailSendCount,
      goldCurrentAttributeIndex,
      goldParentCategory,
      selectedGoldItemType,
    },
    {
      setCurrentStep,
      setCategoryHistory,
      setSelectedCategoryParent,
      setSelectedRootCategory,
      setCurrentItem,
      setItems,
      setIsEditing,
      setSelectedCondition,
      setCurrentAttributeIndex,
      setAttributes,
      setManufacturerOptions,
      setModelOptions,
      setDraftItem,
      setTotalEstimate,
      setquoteId,
      setBrand,
      setopenEstimateCard,
      setOpenExpertReviewAccordion,
      setCategoryItemState,
      setZipCodeFromModal,
      setNearbyStores,
      setSelectedStoreOption,
      setEmailSendCount,
      setGoldCurrentAttributeIndex,
      setGoldParentCategory,
      setSelectedGoldItemType,
      setCategories,
      setHydrated,
      setError,
      setattributeError,
      resetForm,
      fetchCategories,
      fetchManufacturers,
    },
    isSavedQuoteModeRef
  );

  // Set up handler for INVALID_ACCESS_TOKEN error
  useEffect(() => {
    const handleInvalidAccessToken = () => {
      // Show toast message without retry action
      showToast(t.ezquote.sessionExpired, undefined, 'primary');

      // Auto-dismiss toast after 5 seconds
      setTimeout(() => {
        setToastVisible(false);
        setToastMessage("");
      }, 5000);

      // Reset form state with loader
      resetForm(true); // Pass true to show loader
    };

    // Register the callback
    DataRequest.setInvalidAccessTokenCallback(handleInvalidAccessToken);

    // Cleanup on unmount
    return () => {
      DataRequest.setInvalidAccessTokenCallback(() => {});
    };
  }, [fetchData]);

  const resolvedHeader =
    resolveJewelryHeader(goldParentCategory, currentStep, attributes, goldCurrentAttributeIndex, t, brand) ??
    renderHeaderText(currentStep, t);

  // Saved-quote mode reuses FinalReviewStep — the same "estimated quote" screen
  // the live flow ends on — instead of a separate UI. See mapQuotationToItems/
  // mapQuotationStoreToOptions (ez-quote.helpers.ts) for the adapter from the raw
  // GET /quotation/:ezQuoteId response to the Item[]/StoreOption[] shapes that
  // component already renders.
  const effectiveLoading = isSavedQuoteMode ? savedQuoteLoading : loading;
  const effectiveError = isSavedQuoteMode
    ? (!savedQuoteLoading && !savedQuote ? (savedQuoteError || t.savedQuote.notFoundMessage) : null)
    : error;
  // Memoized: FinalReviewStep re-renders often in this mode (accordion toggles,
  // OTP modal state, emailSendCount changes), none of which should re-derive
  // these from savedQuote — only a genuinely new fetched quote should.
  const savedQuoteItems = useMemo(
    () => (isSavedQuoteMode && savedQuote ? mapQuotationToItems(savedQuote, t) : []),
    [isSavedQuoteMode, savedQuote, t]
  );
  const savedQuoteTotalEstimate = useMemo(
    () => (isSavedQuoteMode && savedQuote ? { min: savedQuote.minQuote, max: savedQuote.maxQuote } : { min: 0, max: 0 }),
    [isSavedQuoteMode, savedQuote]
  );
  const savedQuoteNearbyStores = useMemo(
    () => (isSavedQuoteMode && savedQuote ? mapQuotationStoreToOptions(savedQuote) : []),
    [isSavedQuoteMode, savedQuote]
  );
  const handleStartNewQuote = useCallback(() => {
    window.location.href = NEW_QUOTE_ENTRY_PATH;
  }, []);

  return (
    <TranslationProvider value={t}>
    <div className="ezquote-container">
      <Header
        currentStep={
          isSavedQuoteMode
            ? getHeaderStep(totalInternalSteps)
            : goldParentCategory !== null && currentStep === 2 ? 2 : getHeaderStep(currentStep)
        }
        totalSteps={4}
        showBackButton={
          !isSavedQuoteMode && (
            (isOtpModalVisible && otpModalMode === "phone") ||
            (currentStep > 2 && currentStep <= totalInternalSteps) ||
            (currentStep === 2 && (categoryHistory.length > 0 || goldParentCategory !== null))
          )
        }
        customBackAction={handleBackPress}
        onInfoClick={() => setShowIntroModal(true)}
      />
      <div className="main-content">
        {hydrated && !effectiveLoading && !(isOtpModalVisible && otpModalMode === "phone") && (!isSavedQuoteMode || !effectiveError) && (
          <>
            <div className="main-title">{isSavedQuoteMode ? t.headers.step6Title : resolvedHeader.title}</div>
            <div className="subtitle">{isSavedQuoteMode ? t.headers.step6Subtitle : resolvedHeader.subtitle}</div>
          </>
        )}
        {effectiveLoading || !hydrated ? (
          <Loader />
        ) : effectiveError ? (
          <p className="error-message">{effectiveError}</p>
        ) : isOtpModalVisible && otpModalMode === "phone" ? (
          <OtpModalContent
            isVisible={true}
            onClose={() => setIsOtpModalVisible(false)}
            brandName={brand || storesData?.data?.[0]?.brandName || "ezpawn"}
            onVerified={handleOtpVerified}
            onValidateZipAndGetStores={handleValidateZipAndGetStores}
            mode="phone"
          />
        ) : isSavedQuoteMode ? (
          <FinalReviewStep
            items={savedQuoteItems}
            quoteId={quoteId}
            smsDeliveryStatus={null}
            totalEstimate={savedQuoteTotalEstimate}
            error={null}
            nearbyStores={savedQuoteNearbyStores}
            zipCodeFromModal={null}
            brand={brand}
            openExpertReviewAccordion={openExpertReviewAccordion}
            setOpenExpertReviewAccordion={setOpenExpertReviewAccordion}
            openEstimateCard={openEstimateCard}
            setopenEstimateCard={setopenEstimateCard}
            emailSendCount={emailSendCount}
            setOtpModalMode={setOtpModalMode}
            setIsOtpModalVisible={setIsOtpModalVisible}
            scrollToElement={scrollToElement}
            quoteDetailsRef={quoteDetailsRef}
            resetForm={handleStartNewQuote}
            storeAddress={savedQuote?.store?.address ?? null}
            setIsQuoteModalVisible={setIsQuoteModalVisible}
            getIconComponent={getIconComponent}
          />
        ) : (
          renderStep()
        )}
      </div>
      {showIntroModal && (
        <ModalContent
          isVisible={showIntroModal}
          onClose={() => setShowIntroModal(false)}
          onGetStarted={() => setShowIntroModal(false)}
        />
      )}
      {isOtpModalVisible && otpModalMode === "email" && (
        <OtpModalContent
          isVisible={true}
          onClose={() => setIsOtpModalVisible(false)}
          brandName={brand || storesData?.data?.[0]?.brandName || "ezpawn"}
          onVerified={handleOtpVerified}
          onValidateZipAndGetStores={handleValidateZipAndGetStores}
          mode="email"
          quoteId={quoteId}
          items={isSavedQuoteMode ? savedQuoteItems : items}
          totalEstimate={isSavedQuoteMode ? savedQuoteTotalEstimate : totalEstimate}
          onEmailSent={handleEmailSent}
        />
      )}
      <QuoteExplanationModal
        isVisible={isQuoteModalVisible}
        onClose={() => setIsQuoteModalVisible(false)}
      />
      <Toast
        isVisible={toastVisible}
        message={toastMessage}
        onRetry={toastRetryAction}
        onClose={hideToast}
        variant={toastVariant}
      />
    </div>
    </TranslationProvider>
  );
};

export default EZQuote;
